import type { Rating } from "../types/rating";
import { isUnknownArray, dateValueToDate, isTimestamp } from "../types/data";
const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];

export const SHORT_FILM_MAX_RUNTIME_MINUTES = 40;

const normalizeText = (value: unknown) =>
  String(value || "").toLowerCase().replace(/ё/g, "е").trim();

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  // Progress eligibility historically accepts SDK timestamps, Date and primitives.
  if (!(value instanceof Date) && !isTimestamp(value) && typeof value !== "string" && typeof value !== "number") return null;
  return dateValueToDate(value);
};

const getLocalDayKey = (value: unknown) => {
  const date = toDate(value);
  if (!date) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0")
  ].join("-");
};

const getFirstPositiveNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (isUnknownArray(value)) {
      const nested = getFirstPositiveNumber(...value);
      if (nested) return nested;
      continue;
    }

    const number = Number(value);
    if (Number.isFinite(number) && number > 0) return number;
  }

  return null;
};

export const getRuntimeMinutes = (rating: Rating = {}) =>
  getFirstPositiveNumber(
    rating.runtime,
    rating.filmRuntime,
    rating.duration,
    rating.episode_run_time,
    rating.filmData?.runtime,
    rating.filmData?.episode_run_time,
    rating.fullDataFilm?.runtime,
    rating.fullDataFilm?.episode_run_time,
    rating.details?.runtime,
    rating.details?.episode_run_time
  );

const getRatedSeasonEpisodeCount = (rating: Rating = {}) => {
  const seasonRatings = rating.seasonRatings;
  if (!seasonRatings || typeof seasonRatings !== "object") return null;

  const seasons = asArray(rating.seasons);
  return Object.entries(seasonRatings).reduce((total, [seasonKey, seasonRating]) => {
    if (!Number.isFinite(Number(seasonRating?.userRate))) return total;
    const metadata = seasons.find((season) =>
      String(season?.id ?? "") === String(seasonKey) ||
      String(season?.season_number ?? "") === String(seasonKey) ||
      (
        seasonRating?.name &&
        String(season?.name || "") === String(seasonRating.name)
      )
    );
    return total +
      (Number(seasonRating?.episode_count) || Number(metadata?.episode_count) || 0);
  }, 0);
};

const getWholeSeriesEpisodeCount = (rating: Rating = {}) => {
  const directCount = Number(
    rating.number_of_episodes ||
    rating.numberOfEpisodes ||
    rating.filmData?.number_of_episodes ||
    rating.fullDataFilm?.number_of_episodes
  );
  if (Number.isFinite(directCount) && directCount > 0) return directCount;

  return asArray(rating.seasons)
    .filter((season) => Number(season?.season_number) !== 0)
    .reduce((total, season) => total + (Number(season?.episode_count) || 0), 0);
};

export const getWatchedRuntimeMinutes = (rating: Rating = {}) => {
  const mediaType = getMediaType(rating);
  const runtime = mediaType === "tv"
    ? getFirstPositiveNumber(
        rating.episode_run_time,
        rating.episodeRunTime,
        rating.filmData?.episode_run_time,
        rating.fullDataFilm?.episode_run_time,
        rating.details?.episode_run_time,
        rating.runtime,
        rating.filmRuntime,
        rating.duration
      )
    : getRuntimeMinutes(rating);
  if (!runtime) return 0;
  if (mediaType !== "tv") return runtime;

  const ratedSeasonEpisodes = getRatedSeasonEpisodeCount(rating);
  if (ratedSeasonEpisodes != null) {
    return ratedSeasonEpisodes > 0 ? runtime * ratedSeasonEpisodes : runtime;
  }

  const wholeSeriesEpisodes = getWholeSeriesEpisodeCount(rating);
  return runtime * Math.max(1, wholeSeriesEpisodes ?? 0);
};

export const getRatedTitleUnits = (rating: Rating = {}) => {
  if (getMediaType(rating) !== "tv") return 1;
  const seasonRatings = rating.seasonRatings;
  if (!seasonRatings || typeof seasonRatings !== "object") return 1;
  const ratedSeasons = Object.values(seasonRatings).filter(
    (season) => Number.isFinite(Number(season?.userRate))
  ).length;
  return Math.max(1, ratedSeasons);
};

export const getMediaType = (rating: Rating = {}) => {
  const raw = normalizeText(
    rating.mediaType ||
    rating.media_type ||
    rating.type ||
    rating.filmData?.mediaType ||
    rating.filmData?.media_type ||
    rating.fullDataFilm?.media_type
  );

  if (raw.includes("tv") || raw.includes("сериал") || raw.includes("show")) {
    return "tv";
  }

  if (rating.first_air_date || isUnknownArray(rating.seasons)) {
    return "tv";
  }

  return "movie";
};

export const getRatingIdentityKey = (rating: Rating = {}) => {
  const filmId = rating?.filmId ?? rating?.id ?? rating?.tmdbId;
  if (filmId == null || String(filmId).trim() === "") return "";
  return `${getMediaType(rating)}:${String(filmId).trim()}`;
};

export const deduplicateRatingsByMediaId = <T extends Rating>(ratings: T[] = []): T[] => {
  const keyed = new Map<string, T>();
  const anonymous: T[] = [];

  (isUnknownArray(ratings) ? ratings : []).forEach((rating) => {
    const key = getRatingIdentityKey(rating);
    if (key) keyed.set(key, rating);
    else anonymous.push(rating);
  });

  return [...keyed.values(), ...anonymous];
};

const genreNames = (rating: Rating = {}) => [
  ...asArray(rating.genres),
  ...asArray(rating.filmGenres),
  ...asArray(rating.filmData?.genres),
  ...asArray(rating.fullDataFilm?.genres)
]
  .map((genre) => typeof genre === "string" ? genre : genre?.name)
  .map(normalizeText)
  .filter(Boolean);

export const isShortFilm = (rating: Rating = {}) => {
  if (!rating) return false;
  if (rating.isShort === true || rating.shortFilm === true || rating.is_short === true) {
    return true;
  }

  if (genreNames(rating).some((genre) =>
    genre.includes("short") ||
    genre.includes("короткометраж") ||
    genre.includes("короткий метр")
  )) {
    return true;
  }

  if (getMediaType(rating) !== "movie") return false;

  const runtime = getRuntimeMinutes(rating);
  return Boolean(runtime && runtime <= SHORT_FILM_MAX_RUNTIME_MINUTES);
};

export const hasImportMarker = (rating: Rating = {}) =>
  Boolean(rating?.firstImportSource || rating?.importSource || rating?.importedAt);

export const isBackfilledManualRating = (rating: Rating = {}) => {
  if (!rating?.manualWatchedAt) return false;
  const progressMode = rating.firstProgressMode || rating.progressMode;
  if (progressMode !== "current") return false;

  const watchedDay = getLocalDayKey(rating.ratedAt || rating.firstRatedAt);
  const savedDay = getLocalDayKey(rating.updatedAt || rating.createdAt);
  return Boolean(watchedDay && savedDay && watchedDay !== savedDay);
};

export const isCurrentProgressRating = (rating: Rating = {}) => {
  const importSource = rating.firstImportSource || rating.importSource;
  const progressMode = rating.firstProgressMode || rating.progressMode;
  return !(importSource === "ratings-table" && progressMode !== "current");
};

export const isGameProgressEligible = (rating: Rating = {}) => {
  if (!rating || rating.active === false || isShortFilm(rating)) return false;
  if (!isCurrentProgressRating(rating)) return false;
  if ((rating.firstProgressMode || rating.progressMode) === "archive") return false;
  if ((rating.firstStreakEligible ?? rating.streakEligible) === false) return false;
  return true;
};

export const filterGameProgressRatings = (ratings: Rating[] = []) =>
  deduplicateRatingsByMediaId(
    (isUnknownArray(ratings) ? ratings : []).filter(isGameProgressEligible)
  );

export const isExperienceEligible = (rating: Rating = {}) =>
  Boolean(rating) && rating.active !== false && !isShortFilm(rating);

export const filterExperienceRatings = (ratings: Rating[] = []) =>
  deduplicateRatingsByMediaId(
    (isUnknownArray(ratings) ? ratings : []).filter(isExperienceEligible)
  );
